import React from 'react';
import { SSOLoginButton } from './buttons';
import { SSOImg } from './SSOImg';
import { SSOLoginSpan } from './formSpans';

export type SsoProviderId =
  | 'xero'
  | 'google'
  | 'azure'
  | 'quickbooks'
  | 'zoho';

export interface SsoProviderButtonProps {
  provider: SsoProviderId;
  label: string;
  iconSrc: string;
  /** Intuit official “Sign in with Intuit” artwork, shown on hover/focus. */
  hoverIconSrc?: string;
  onClick: () => void;
  minWidth?: string;
  marginRight?: string;
  className?: string;
}

export const SsoProviderButton: React.FC<SsoProviderButtonProps> = ({
  provider,
  label,
  iconSrc,
  hoverIconSrc,
  onClick,
  minWidth,
  marginRight,
  className,
}) => {
  const isQbo = provider === 'quickbooks';
  const isMicrosoft = provider === 'azure';

  return (
    <SSOLoginButton
      htmlType="button"
      className={[isQbo ? 'scope-sso-login-button--qbo' : '', className]
        .filter(Boolean)
        .join(' ')}
      aria-label={label}
      onClick={onClick}
      minwidth={minWidth}
      marginright={marginRight}
    >
      <SSOImg
        src={iconSrc}
        alt=""
        aria-hidden
        width={isQbo ? '36px' : undefined}
        height={isMicrosoft ? 36 : undefined}
      />
      <SSOLoginSpan className={isQbo ? 'scope-sso-qbo-label' : undefined}>
        {label}
      </SSOLoginSpan>
      {isQbo && hoverIconSrc ? (
        <img
          className="scope-sso-qbo-hover"
          src={hoverIconSrc}
          alt=""
          aria-hidden
        />
      ) : null}
    </SSOLoginButton>
  );
};

export type SsoProviderListItem = Omit<
  SsoProviderButtonProps,
  'className' | 'minWidth' | 'marginRight'
>;

export interface SsoProviderListProps {
  items: SsoProviderListItem[];
  /** `stack` = login column; `wrap` = signup 3+2 rows (stacks when narrow). */
  layout?: 'stack' | 'wrap';
  showZoho?: boolean;
}

export const SsoProviderList: React.FC<SsoProviderListProps> = ({
  items,
  layout = 'stack',
  showZoho = true,
}) => {
  const visible = items.filter(
    (item) => item.provider !== 'zoho' || showZoho,
  );

  if (layout === 'wrap') {
    const firstRow = visible.slice(0, 3);
    const secondRow = visible.slice(3);

    return (
      <div className="scope-signup-sso">
        <div className="scope-signup-sso__row">
          {firstRow.map((item) => (
            <SsoProviderButton key={item.provider} {...item} />
          ))}
        </div>
        {secondRow.length > 0 ? (
          <div className="scope-signup-sso__row">
            {secondRow.map((item) => (
              <SsoProviderButton key={item.provider} {...item} />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="scope-sso-login-stack">
      {visible.map((item) => (
        <SsoProviderButton key={item.provider} {...item} />
      ))}
    </div>
  );
};

interface AuthSsoCopy {
  xeroLogin: string;
  googleLogin: string;
  microsoftLogin: string;
  qboLogin: string;
  zohoLogin: string;
}

interface AuthSsoAssets {
  xero: string;
  google: string;
  microsoft: string;
  qbo: string;
  qboHover?: string;
  zoho: string;
}

/** Shared Xero → Google → Microsoft → Intuit → Zoho order for login and signup. */
export function authSsoProviders({
  labels,
  assets,
  onProviderLogin,
}: {
  labels: AuthSsoCopy;
  assets: AuthSsoAssets;
  onProviderLogin: (provider: SsoProviderId) => void;
}): SsoProviderListItem[] {
  return [
    {
      provider: 'xero',
      label: labels.xeroLogin,
      iconSrc: assets.xero,
      onClick: () => onProviderLogin('xero'),
    },
    {
      provider: 'google',
      label: labels.googleLogin,
      iconSrc: assets.google,
      onClick: () => onProviderLogin('google'),
    },
    {
      provider: 'azure',
      label: labels.microsoftLogin,
      iconSrc: assets.microsoft,
      onClick: () => onProviderLogin('azure'),
    },
    {
      provider: 'quickbooks',
      label: labels.qboLogin,
      iconSrc: assets.qbo,
      hoverIconSrc: assets.qboHover,
      onClick: () => onProviderLogin('quickbooks'),
    },
    {
      provider: 'zoho',
      label: labels.zohoLogin,
      iconSrc: assets.zoho,
      onClick: () => onProviderLogin('zoho'),
    },
  ];
}
